import SEO from '../components/SEO';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiUser, FiArrowLeft, FiShare2, FiHeart, FiPlay, FiCalendar } from 'react-icons/fi';
import { FaYoutube } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { fetchInfomaticById, fetchInfomatics, getImageSrc} from '../apiService';

const InfomaticDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [infomatic, setInfomatic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedInfomatics, setRelatedInfomatics] = useState([]);
  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    loadInfomatic();
    window.scrollTo(0, 0);
  }, [id]);

  useEffect(() => {
    if (infomatic) {
      loadRelatedInfomatics();
    }
  }, [infomatic]);

  const loadInfomatic = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // First try to fetch the specific infomatic by ID
      let foundInfomatic = await fetchInfomaticById(id);
      
      // If that fails, fallback to the old method
      if (!foundInfomatic) {
        console.log('Direct fetch failed, trying fallback method...');
        const infomaticsData = await fetchInfomatics();
        foundInfomatic = infomaticsData.find(item => item._id === id || item.slug === id);
      }
      
      if (foundInfomatic) {
        setInfomatic(foundInfomatic);
      } else {
        setError('Infomatic not found');
        console.error('Infomatic not found with ID:', id);
        // Don't navigate immediately, let user see the error
        setTimeout(() => {
          navigate('/allinfomatics');
        }, 3000);
      }
    } catch (err) {
      console.error('Error loading infomatic:', err);
      setError(`Failed to load infomatic: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadRelatedInfomatics = async () => {
    try {
      const infomaticsData = await fetchInfomatics();
      const filtered = infomaticsData.filter(relatedInfomatic => relatedInfomatic._id !== id);
      setRelatedInfomatics(filtered.slice(0, 3));
    } catch (err) {
      console.error('Error loading related infomatics:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('mr-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: infomatic.title,
          text: infomatic.description[0] || '',
          url: window.location.href
        });
      } catch (err) {
        console.log('Error sharing:', err);
        copyToClipboard();
      }
    } else {
      copyToClipboard();
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Link copied to clipboard!');
  };

  const handleLike = () => {
    setIsLiked(!isLiked);
  };

  const openYouTubeVideo = (url) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-20">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-300 rounded mb-4 w-1/4"></div>
              <div className="h-12 bg-gray-300 rounded mb-6"></div>
              <div className="h-64 bg-gray-300 rounded mb-6"></div>
              <div className="space-y-4">
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded w-5/6"></div>
                <div className="h-4 bg-gray-300 rounded w-4/6"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !infomatic) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <div className="text-6xl mb-4">😕</div>
          <h2 className="text-3xl font-bold text-red-800 mb-4">ज्ञानराशी सापडली नाही</h2>
          <p className="text-red-600 mb-2">ID: {id}</p>
          <p className="text-red-600 mb-6 text-sm">{error}</p>
          <div className="space-x-4">
            <button 
              onClick={() => navigate(-1)}
              className="bg-red-800 text-white px-6 py-2 rounded-lg hover:bg-red-900 transition-colors"
            >
              मागे जा
            </button>
            <button 
              onClick={() => navigate('/allinfomatics')}
              className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700 transition-colors inline-block"
            >
              सर्व ज्ञानराशी
            </button>
          </div>
          
          {/* Debug info for development */}
          {process.env.NODE_ENV === 'development' && (
            <div className="mt-6 p-4 bg-gray-100 rounded text-left text-xs">
              <strong>Debug Info:</strong><br/>
              URL ID: {id}<br/>
              Error: {error}<br/>
              Current URL: {window.location.href}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-20">
      <SEO title={infomatic.title} description={infomatic.content?.[0] ? infomatic.content[0].replace(/<[^>]+>/g, '').slice(0, 155) : 'डॉ. राजेंद्र शेंडे यांची माहिती.'} image={infomatic.thumbnail} canonical={`/infomatic/${infomatic.slug || infomatic._id}`} type="article" article={{ publishedTime: infomatic.date }} breadcrumbs={[{ name: 'मुख्यपृष्ठ', url: '/' }, { name: 'इन्फोमॅटिक्स', url: '/allinfomatics' }, { name: infomatic.title, url: `/infomatic/${infomatic.slug || infomatic._id}` }]} />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.div 
            className="mb-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
          >
            <button 
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 text-red-800 hover:text-red-900 font-medium transition-colors"
            >
              <FiArrowLeft className="w-5 h-5" />
              मागे जा
            </button>
          </motion.div>

          {/* Rest of your existing component JSX remains the same */}
          <motion.article 
            className="bg-white border-2 border-red-100 rounded-2xl shadow-xl overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {/* Hero Image */}
            <div className="relative h-64 md:h-96 overflow-hidden">
              <img loading="lazy" 
                src={getImageSrc(infomatic.thumbnail)} 
                alt={infomatic.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              
              {/* Video Indicator */}
              {infomatic.videoUrl && (
                <div className="absolute top-6 right-6">
                  <div className="bg-red-800 text-white p-3 rounded-full shadow-lg">
                    <FiPlay className="w-6 h-6" />
                  </div>
                </div>
              )}
              
              {/* Title Overlay */}
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <motion.h1 
                  className="text-2xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  {infomatic.title}
                </motion.h1>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 md:p-8">
              {/* Meta Information */}
              <motion.div 
                className="flex flex-wrap items-center gap-4 md:gap-6 text-red-700 mb-6 pb-6 border-b border-red-200"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <div className="flex items-center gap-2">
                  <FiUser className="w-5 h-5 text-red-600" />
                  <span className="font-medium">{infomatic.author || 'Admin'}</span>
                </div>
                <div className="flex items-center gap-2">
                  <FiCalendar className="w-5 h-5 text-red-600" />
                  <span>{formatDate(infomatic.date)}</span>
                </div>
              </motion.div>

              {/* Infomatic Content */}
              <motion.div 
                className="prose prose-lg max-w-none"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
              >
                {infomatic.description && infomatic.description.map((paragraph, index) => (
                  <p key={index} className="mb-6 text-red-800 leading-relaxed text-lg">
                    {paragraph}
                  </p>
                ))}
              </motion.div>

              {/* Additional Images */}
              {infomatic.images && infomatic.images.length > 0 && (
                <motion.div 
                  className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  {infomatic.images.map((image, index) => (
                    <div key={index} className="rounded-xl overflow-hidden shadow-lg border-2 border-red-100">
                      <img loading="lazy" 
                        src={getImageSrc(image)} 
                        alt={`${infomatic.title} - Image ${index + 1}`}
                        className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                  ))}
                </motion.div>
              )}

              {/* Video Section */}
              {infomatic.videoUrl && (
                <motion.div 
                  className="mt-8 p-6 bg-red-50 border-2 border-red-200 rounded-xl"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 1.1 }}
                >
                  <h3 className="text-lg font-bold text-red-800 mb-4 flex items-center gap-2">
                    <FaYoutube className="w-6 h-6 text-red-600" />
                    संबंधित व्हिडिओ
                  </h3>
                  <button
                    onClick={() => openYouTubeVideo(infomatic.videoUrl)}
                    className="inline-flex items-center gap-3 bg-red-800 hover:bg-red-900 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl"
                  >
                    <FaYoutube className="w-5 h-5" />
                    व्हिडिओ पाहा
                  </button>
                </motion.div>
              )}

              {/* Action Buttons */}
              <motion.div 
                className="flex items-center justify-between pt-8 mt-8 border-t border-red-200"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 1.2 }}
              >
                <div className="flex items-center gap-4">
                  <button 
                    onClick={handleLike}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                      isLiked 
                        ? 'bg-red-800 text-white shadow-lg' 
                        : 'bg-red-100 text-red-800 hover:bg-red-50 hover:text-red-700'
                    }`}
                  >
                    <FiHeart className={`w-5 h-5 ${isLiked ? 'fill-current' : ''}`} />
                    आवडले
                  </button>
                  <button 
                    onClick={handleShare}
                    className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-800 rounded-lg font-medium hover:bg-blue-50 hover:text-blue-700 transition-all duration-300"
                  >
                    <FiShare2 className="w-5 h-5" />
                    शेअर करा
                  </button>
                </div>
                <button 
                  onClick={() => navigate('/allinfomatics')}
                  className="text-red-800 hover:text-red-900 font-medium transition-colors"
                >
                  सर्व ज्ञानराशी पहा
                </button>
              </motion.div>
            </div>
          </motion.article>

          {/* Related Infomatics */}
          {relatedInfomatics.length > 0 && (
            <motion.div 
              className="mt-12"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.4 }}
            >
              <h2 className="text-2xl md:text-3xl font-bold text-red-800 mb-8 text-center">
                संबंधित ज्�ानराशी
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {relatedInfomatics.map((relatedInfomatic, index) => (
                  <motion.div
                    key={relatedInfomatic._id}
                    className="bg-white border-2 border-red-100 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group cursor-pointer"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 1.6 + index * 0.1 }}
                    whileHover={{ y: -5 }}
                    onClick={() => {
                      if (relatedInfomatic.videoUrl) {
                        openYouTubeVideo(relatedInfomatic.videoUrl);
                      } else {
                        navigate(`/infomatic/${relatedInfomatic.slug || relatedInfomatic._id}`);
                      }
                    }}
                  >
                    <div className="relative h-40 overflow-hidden">
                      {relatedInfomatic.videoUrl && (
                        <div className="absolute top-2 left-2 z-10">
                          <div className="bg-red-800 text-white p-1.5 rounded-full shadow-lg">
                            <FiPlay className="w-3 h-3" />
                          </div>
                        </div>
                      )}
                      <img loading="lazy" 
                        src={getImageSrc(relatedInfomatic.thumbnail)} 
                        alt={relatedInfomatic.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-red-800 mb-2 line-clamp-2 group-hover:text-red-600 transition-colors">
                        {relatedInfomatic.title}
                      </h3>
                      <p className="text-red-700 text-sm mb-3 line-clamp-2">
                        {Array.isArray(relatedInfomatic.description) 
                          ? relatedInfomatic.description.join(' ').substring(0, 100) + '...'
                          : relatedInfomatic.description?.substring(0, 100) + '...'
                        }
                      </p>
                      <div className="flex items-center justify-between text-xs text-red-600">
                        <span>{relatedInfomatic.author || 'Admin'}</span>
                        <span className="flex items-center gap-1">
                          {relatedInfomatic.videoUrl ? (
                            <>
                              <FiPlay className="w-3 h-3" />
                              पाहा
                            </>
                          ) : (
                            <>
                              <FiArrowLeft className="w-3 h-3 transform rotate-180" />
                              वाचा
                            </>
                          )}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfomaticDetails;